import { useState, useEffect } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { NumberInput, ActionIcon, Tooltip, Group, Text } from '@mantine/core';
import { IconTrash, IconEdit, IconCheck, IconX } from '@tabler/icons-react';
import type { IConfiguracionMoiEscalera } from '../interfaces';

export interface MoiEscaleraTableMeta {
  actualizarFila: (id: string, campo: keyof IConfiguracionMoiEscalera, valor: string | number) => void;
  eliminarFila: (id: string) => void;
  guardarFila?: (id: string) => void;
  filaEnEdicion?: string | null;
  iniciarEdicion?: (fila: any) => void;
  cancelarEdicion?: () => void;
  mostrarNotificacionObligatorio?: () => void;
  intentoGuardar?: boolean;
  /** Moneda del costo: la moneda local del país (COP en Colombia, USD en países en dólares). */
  monedaLocal?: string;
}

/** Días con su unidad: "1 día", "15 días". */
const formatoDias = (dias: number) => `${dias.toLocaleString('es-CO')} ${dias === 1 ? 'día' : 'días'}`;

const EditableTextCell = ({ getValue, row, column, table }: any) => {
  const initialValue = getValue() as string;
  const [value, setValue] = useState(initialValue);
  const [touched, setTouched] = useState(false);
  const meta = table.options.meta as MoiEscaleraTableMeta | undefined;
  const campo = column.id as keyof IConfiguracionMoiEscalera;
  const rowId = row.original.Id;

  useEffect(() => { setValue(initialValue); }, [initialValue]);

  const isEditing = String(meta?.filaEnEdicion) === String(rowId);
  if (!isEditing) {
    return <Text size="xs">{value ? `${Number(value).toLocaleString('es-CO')} mm` : "-"}</Text>;
  }
  // Solo números mayores a 0 con hasta 2 decimales, igual que la carga masiva.
  return (
    <NumberInput
      size="xs"
      hideControls
      value={value}
      onChange={(val) => { setTouched(true); setValue(String(val)); if (meta) meta.actualizarFila(rowId, campo, val === "" ? "" : Number(val)); }}
      placeholder={`${campo} *`}
      required
      withAsterisk
      allowNegative={false}
      decimalScale={2}
      thousandSeparator
      suffix=" mm"
      error={(touched || meta?.intentoGuardar) && (!value || String(value).trim() === "" || Number(value) <= 0)}
      styles={{ input: { fontSize: "12px", padding: "4px 8px" } }}
    />
  );
};

const EditableNumberCell = ({ getValue, row, column, table }: any) => {
  const initialValue = getValue() as number;
  const [value, setValue] = useState<number | string>(initialValue);
  const [touched, setTouched] = useState(false);
  const meta = table.options.meta as MoiEscaleraTableMeta | undefined;
  const campo = column.id as keyof IConfiguracionMoiEscalera;
  const rowId = row.original.Id;

  useEffect(() => { setValue(initialValue); }, [initialValue]);

  const isEditing = String(meta?.filaEnEdicion) === String(rowId);
  if (!isEditing) {
    if (value === null || value === undefined || value === "") return <Text size="xs">-</Text>;
    return <Text size="xs">{campo === 'Costo' ? `$ ${Number(value).toLocaleString('es-CO')} ${meta?.monedaLocal ?? 'COP'}` : formatoDias(Number(value))}</Text>;
  }
  return (
    <NumberInput
      size="xs"
      hideControls
      value={value !== 0 ? value : (value === 0 ? "0" : "")}
      onChange={(val) => { setTouched(true); setValue(val); if (meta) meta.actualizarFila(rowId, campo, val === "" ? "" : (Number(val) || 0)); }}
      placeholder={`${campo} *`}
      required
      withAsterisk
      error={(touched || meta?.intentoGuardar) && (value === null || value === undefined || String(value) === "")}
      styles={{ input: { fontSize: "12px", padding: "4px 8px" } }}
      thousandSeparator
      prefix={campo === 'Costo' ? "$ " : undefined}
      suffix={campo === 'Costo' ? ` ${meta?.monedaLocal ?? 'COP'}` : " días"}
    />
  );
};

export const obtenerColumnasMoiEscalera = (): ColumnDef<IConfiguracionMoiEscalera>[] => [
  {
    id: "acciones",
    header: "Acciones",
    size: 90,
    cell: ({ row, table }) => {
      const meta = table.options.meta as MoiEscaleraTableMeta | undefined;
      const isEditing = String(meta?.filaEnEdicion) === String(row.original.Id);
      const hayOtraFilaEnEdicion = meta?.filaEnEdicion !== null && meta?.filaEnEdicion !== undefined && String(meta?.filaEnEdicion) !== String(row.original.Id);
      
      if (isEditing) {
        return (
          <Group gap={4} justify="center">
            <Tooltip label="Guardar" withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
              <ActionIcon variant="subtle" color="var(--mantine-color-primary-0)" size="sm" radius="xl" onClick={() => {
                const fila = row.original;
                if (!fila.Altura || String(fila.Altura).trim() === "" || Number(fila.Altura) <= 0 || fila.Costo === null || fila.Costo === undefined || String(fila.Costo) === "" || fila.Tiempo === null || fila.Tiempo === undefined || String(fila.Tiempo) === "") {
                  meta?.mostrarNotificacionObligatorio?.();
                  return;
                }
                meta?.guardarFila?.(fila.Id);
              }}>
                <IconCheck size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Cancelar" withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
              <ActionIcon variant="subtle" color="#df675a" size="sm" radius="xl" onClick={() => meta?.cancelarEdicion?.()}>
                <IconX size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        );
      }
      return (
        <Group gap={4} justify="center">
          <Tooltip label={hayOtraFilaEnEdicion ? "Termina la edición actual" : "Editar"} withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
             <ActionIcon disabled={hayOtraFilaEnEdicion} variant="transparent" color="var(--mantine-color-primary-0)" size="sm" radius="sm" onClick={() => meta?.iniciarEdicion?.(row.original)}>
               <IconEdit size={18} />
             </ActionIcon>
          </Tooltip>
          <Tooltip label={hayOtraFilaEnEdicion ? "Termina la edición actual" : "Eliminar"} withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
            <ActionIcon disabled={hayOtraFilaEnEdicion} variant="transparent" color="var(--mantine-color-primary-0)" size="sm" radius="sm" onClick={() => meta?.eliminarFila(row.original.Id)}>
              <IconTrash size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      );
    }
  },
  {
    accessorKey: "Altura",
    header: "Altura",
    cell: EditableTextCell,
    size: 150,
  },
  {
    accessorKey: "Costo",
    header: "M.O.D (Costo Fijo)",
    cell: EditableNumberCell,
    size: 200,
  },
  {
    accessorKey: "Tiempo",
    header: "Tiempo",
    cell: EditableNumberCell,
    size: 150,
  }
];
